import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { List, ListItem, ListItemText, Collapse, useMediaQuery } from "@mui/material";
import { GoPackage } from "react-icons/go";
import { RiSecurePaymentLine } from "react-icons/ri";
import { BsCashStack } from "react-icons/bs";
import { BsChatDots } from "react-icons/bs";
import { ReactComponent as ProjectIcon } from "../../../assets/svg/ProjectIcon.svg";
import { ReactComponent as ProposalIcon } from "../../../assets/svg/ProposalIcon.svg";
import { ReactComponent as PaymentIcon } from "../../../assets/svg/PaymentIcon.svg";
import { ReactComponent as ProfileIcon } from "../../../assets/svg/ProfileIcon.svg";
import { ReactComponent as ProjectActiveIcon } from "../../../assets/svg/ProjectActiveIcon.svg";
import { ReactComponent as ProposalActiveIcon } from "../../../assets/svg/ProposalActiveIcon.svg";
import { ReactComponent as PaymentActiveIcon } from "../../../assets/svg/PaymentActiveIcon.svg";
import { ReactComponent as ProfileActiveIcon } from "../../../assets/svg/ProfileActiveIcon.svg";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useCRUDPermission } from "../../hooks/usePermission";


export default function DrawerList({ handleMobileDrawerClose, isMobile }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openProfileMenu, setOpenProfileMenu] = useState(false);

  // Check permissions for each route
  const { hasPermission: hasProjectsPermission } = useCRUDPermission("PROJECTS", "READ");
  const { hasPermission: hasProposalsPermission } = useCRUDPermission("PROJECTS", "READ"); // Using PROJECTS for now, adjust if PROPOSALS exists
  const { hasPermission: hasPackagesPermission } = useCRUDPermission("PACKAGES", "READ"); // Using PACKAGES resource for packages
  const { hasPermission: hasPaymentsPermission } = useCRUDPermission("CUSTOMER_PAYMENTS", "READ");
  const { hasPermission: hasBalancePermission } = useCRUDPermission("CONTRACTOR_PAYOUTS", "READ");

  const isActive = (path) => pathname === path;
  const isProfileActive = (path) =>
    pathname.startsWith("/payments") ||
    pathname.startsWith("/balance-breakdown");

  const handleNavigation = (path) => {
    navigate(path);
    if (handleMobileDrawerClose) {
      handleMobileDrawerClose();
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFF",
          fontFamily: "Inter Tight !important",
        }}
      >
        <List style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {/* Projects - Only show if user has PROJECTS READ permission */}
          {hasProjectsPermission && (
            <ListItem
              button
              onClick={() => handleNavigation("/dashboard")}
              style={{
                color: isActive("/ongoing-project" && "/dashboard")
                  ? "#4D6BF6"
                  : "#949DAA",
                fontWeight: "600 !important",
                fontSize: "14px !important",
              }}
            >
              {isActive("/ongoing-project" && "/dashboard") ? (
                <ProjectActiveIcon
                  style={{ marginRight: 15, fontSize: "18px" }}
                />
              ) : (
                <ProjectIcon style={{ marginRight: 15, fontSize: "18px" }} />
              )}
              <ListItemText primary="Projects" />
            </ListItem>
          )}

          <ListItem
            button
            onClick={() => handleNavigation("/conversation")}
            style={{
              color: isActive("/conversation") ? "#4D6BF6" : "#949DAA",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            <BsChatDots style={{ marginRight: 15, fontSize: "18px" }} />
            <ListItemText primary="Chat" />
          </ListItem>

          {/* Proposals - Only show if user has PROJECTS READ permission */}
          {hasProposalsPermission && (
            <ListItem
              button
              onClick={() => handleNavigation("/proposals")}
              style={{
                color: isActive("/proposals") ? "#4D6BF6" : "#949DAA",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              {isActive("/proposals") ? (
                <ProposalActiveIcon
                  style={{ marginRight: 15, fontSize: "18px" }}
                />
              ) : (
                <ProposalIcon style={{ marginRight: 15, fontSize: "18px" }} />
              )}
              <ListItemText primary="Proposals" />
            </ListItem>
          )}

          {/* Packages - Only show if user has PACKAGES READ permission */}
          {!isMobile && hasPackagesPermission && (
            <ListItem
              button
              onClick={() => handleNavigation("/packages")}
              style={{
                color: isActive("/packages") ? "#4D6BF6" : "#949DAA",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              <GoPackage style={{ marginRight: 15, fontSize: "20px" }} />
              <ListItemText primary="Packages" />
            </ListItem>
          )}

          {/* Payments Menu - Only show if user has any payment-related permission */}
          {(hasPaymentsPermission || hasBalancePermission) && (
            <>
              <ListItem
                button
                onClick={() => setOpenProfileMenu(!openProfileMenu)}
                style={{
                  color: isProfileActive() ? "#4D6BF6" : "#949DAA",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {isProfileActive() ? (
                  <PaymentActiveIcon
                    style={{ marginRight: 15, fontSize: "18px" }}
                  />
                ) : (
                  <PaymentIcon style={{ marginRight: 15, fontSize: "18px" }} />
                )}
                <ListItemText primary="Payments" />
                {openProfileMenu ? (
                  <ExpandLess style={{ marginLeft: "auto", fontSize: "18px" }} />
                ) : (
                  <ExpandMore style={{ marginLeft: "auto", fontSize: "18px" }} />
                )}
              </ListItem>
              <Collapse in={openProfileMenu}>
                <List sx={{ marginLeft: "20px" }}>
                  {/* All Payments - Only show if user has CUSTOMER_PAYMENTS READ permission */}
                  {hasPaymentsPermission && (
                    <ListItem
                      button
                      onClick={() => handleNavigation("/payments")}
                      style={{
                        color: isActive("/payments") ? "#4D6BF6" : "#949DAA",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      <RiSecurePaymentLine
                        style={{ marginRight: 14, fontSize: "22px" }}
                      />
                      <ListItemText primary="All Payments" />
                    </ListItem>
                  )}

                  {/* Project Balance - Only show if user has CONTRACTOR_PAYOUTS READ permission */}
                  {hasBalancePermission && (
                    <ListItem
                      button
                      onClick={() => handleNavigation("/balance-breakdown")}
                      style={{
                        color: isActive("/balance-breakdown") ? "#4D6BF6" : "#949DAA",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      <BsCashStack style={{ marginRight: 15, fontSize: "20px" }} />
                      <ListItemText primary="Project Balance" />
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </div>
    </>
  );
}
